import {
  Board,
  CURRENCY,
  QuoteItem,
  QuoteState,
} from "../../../../reducers/uploadedBoard";
import { QuoteToolContainer, QuoteToolTitle } from "../QuoteStyles";
import { BreakdownContainer, ExpandText, GroupTitle } from "./BreakdownStyles";
import FlashPCBCollapsible from "../../../common/FlashPCBCollapsible";
import { useAppSelector } from "../../../../utils/hooks/store";
import { Fragment, useContext } from "react";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import LineItem from "../../../common/LineItem";
import { formatCurrency } from "../../../../utils/valueFormatting";

export function QuoteItemChildren({
  item,
  parents,
  currency,
  updatingQuote,
}: {
  item: QuoteItem;
  parents: string[];
  currency: CURRENCY;
  updatingQuote?: boolean;
}) {
  if (item.amount == 0) {
    return null;
  }
  if (item.children.length == 0) {
    return (
      <LineItem
        key={item.id}
        leftText={item.name}
        rightText={formatCurrency(item.amount, currency)}
        description={item.description}
        isUpdating={updatingQuote}
      />
    );
  }

  const path = [...parents, item.name];
  const groupTitle = path.slice(1).join(" ");

  const breakdown = (
    <BreakdownContainer depth={parents.length}>
      {groupTitle && <GroupTitle>{groupTitle} Breakdown</GroupTitle>}
      {item.children.map((child) => {
        return (
          <QuoteItemChildren
            key={child.id}
            parents={path}
            item={child}
            currency={currency}
            updatingQuote={updatingQuote}
          />
        );
      })}
    </BreakdownContainer>
  );

  return (
    <>
      {parents.length > 0 && (
        <LineItem
          key={item.id}
          leftText={item.name}
          rightText={formatCurrency(item.amount, currency)}
          description={item.description}
          isUpdating={updatingQuote}
        />
      )}
      {item.children.length > 7 ? (
        <FlashPCBCollapsible
          renderTrigger={(isExpanded) => {
            return (
              <ExpandText>
                {isExpanded
                  ? `Hide ${item.name} details`
                  : `Show ${item.name} details`}
              </ExpandText>
            );
          }}
        >
          {breakdown}
        </FlashPCBCollapsible>
      ) : (
        breakdown
      )}
    </>
  );
}

export function QuoteBreakdownItems({
  items,
  currency,
  updatingQuote,
}: {
  items: QuoteItem[];
  currency: CURRENCY;
  updatingQuote?: boolean;
}) {
  return (
    <BreakdownContainer depth={0}>
      {items.map((item) => {
        if (item.amount == 0) {
          return null;
        }
        if (item.children.length == 0) {
          return (
            <LineItem
              key={item.id}
              leftText={item.name}
              rightText={formatCurrency(item.amount, currency)}
              description={item.description}
              isUpdating={updatingQuote}
            />
          );
        }
        return (
          <Fragment key={item.id}>
            <QuoteItemChildren
              parents={[]}
              item={item}
              currency={currency}
              updatingQuote={updatingQuote}
            />
            <LineItem
              leftText={item.name}
              rightText={formatCurrency(item.amount, currency)}
              description={item.description}
              isUpdating={updatingQuote}
            />
          </Fragment>
        );
      })}
    </BreakdownContainer>
  );
}

export default function QuoteBreakdown() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const board = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.board || null,
  ) as Board;
  const isProcessingQuote =
    useAppSelector(
      (state) => state.uploadedBoard.boards[boardId]?.isProcessing || false,
    ) && board.quote.state != QuoteState.ORDER_RECEIVED;

  if (board.quote.items.length === 0) {
    return null;
  }

  return (
    <QuoteToolContainer>
      <QuoteToolTitle>Quote breakdown</QuoteToolTitle>
      <QuoteBreakdownItems
        items={board.quote.items}
        currency={board.quote.currency}
        updatingQuote={isProcessingQuote}
      />
    </QuoteToolContainer>
  );
}
